const menuRouteRegistry = {
  "/finance/partner-commission": {
    name: "PartnerCommission",
    component: () => import("../views/finance/partner-commission/index.vue"),
    menuPermission: "finance:partner-commission:manage",
    permissionsAll: ["finance:partner-commission:list"],
  },
  "/dashboard": {
    name: "Dashboard",
    component: () => import("../views/dashboard/index.vue"),
  },

  // =========================================================
  // 系统管理
  // =========================================================
  "/system/user": {
    name: "SystemUser",
    component: () => import("../views/system/user/index.vue"),
  },
  "/system/role": {
    name: "SystemRole",
    component: () => import("../views/system/role/index.vue"),
  },
  "/system/permission": {
    name: "SystemPermission",
    component: () => import("../views/system/permission/index.vue"),
  },
  "/system/log": {
    name: "SystemLog",
    component: () => import("../views/system/log/index.vue"),
  },
  // =========================================================
  // 小程序用户
  // =========================================================
  "/portal-user": {
    name: "PortalUser",
    component: () => import("../views/portal-user/index.vue"),
    menuPermission: "portal:user:manage",
    permissionsAll: ["portal:user:list"],
  },
  "/portal-user/ai-quota-applies": {
    name: "PortalUserAiQuotaApplies",
    component: () => import("../views/portal-user/ai-quota-applies.vue"),
    menuPermission: "portal:user:quota",
  },

  // =========================================================
  // 定制游
  // =========================================================
  "/custom-tour": {
    name: "CustomTour",
    component: () => import("../views/custom-tour/index.vue"),
  },
  "/custom-tour/review": {
    name: "CustomTourReview",
    component: () => import("../views/custom-tour/review/index.vue"),
  },

  // =========================================================
  // 旅游资源
  // =========================================================
  "/travel/hotel": {
    name: "TravelHotel",
    component: () => import("../views/travel/hotel/index.vue"),
  },

  // =========================================================
  // 供应商资料中心
  // =========================================================

  /**
   * 供应商管理
   *
   * 菜单权限：
   * supplier:base
   *
   * 页面查询权限：
   * supplier:base:list
   */
  "/supplier/supplier": {
    name: "Supplier",
    component: () => import("../views/supplier/supplier/index.vue"),
    menuPermission: "supplier:base",
    permissionsAll: ["supplier:base:list"],
  },

  /**
   * 供应商用户管理
   *
   * 菜单权限：
   * supplier:user
   *
   * 页面查询权限：
   * supplier:user:list
   */
  "/supplier/user": {
    name: "SupplierUser",
    component: () => import("../views/supplier/user/index.vue"),
    menuPermission: "supplier:user",
    permissionsAll: ["supplier:user:list"],
  },

  /**
   * 资料管理
   *
   * 菜单权限：
   * supplier:material
   *
   * 页面查询权限：
   * supplier:material:list
   */
  "/supplier/material": {
    name: "SupplierMaterial",
    component: () => import("../views/supplier/material/index.vue"),
    menuPermission: "supplier:material",
    permissionsAll: ["supplier:material:list"],
  },

  /**
   * 资源库
   *
   * 菜单权限：
   * supplier:resource
   *
   * 页面查询权限：
   * supplier:resource:list
   */
  "/supplier/resource": {
    name: "SupplierResource",
    component: () => import("../views/supplier/resource/index.vue"),
    menuPermission: "supplier:resource",
    permissionsAll: ["supplier:resource:list"],
  },

  // =========================================================
  // Tour V2 旅游业务
  // =========================================================
  "/tour/requirement": {
    name: "TourRequirement",
    component: () => import("../views/tour/requirement/index.vue"),
    menuPermission: "tour:requirement",
    permissionsAll: ["tour:requirement:list"],
    detailRoutes: [
      {
        path: "/tour/requirement/:requirementId",
        name: "TourRequirementWorkbench",
        component: () => import("../views/tour/requirement/workbench.vue"),
        title: "定制游工作台",
        permissionsAll: ["tour:requirement:detail"],
      },
    ],
  },

  "/tour/order": {
    name: "TourOrder",
    component: () => import("../views/tour/order/index.vue"),
    menuPermission: "tour:order",
    permissionsAll: ["tour:order:list"],
    detailRoutes: [
      {
        path: "/tour/order/:orderId",
        name: "TourOrderDetail",
        component: () => import("../views/tour/order/detail.vue"),
        title: "旅游订单详情",
        permissionsAll: ["tour:order:detail"],
      },
    ],
  },
};

/**
 * 判断是否拥有指定权限。
 *
 * "*:*:*" 为超级管理员权限。
 */
const hasPermissionValue = (permissions = [], permission) =>
  !permission ||
  permissions.includes("*:*:*") ||
  permissions.includes(permission);

/**
 * 标准化菜单地址。
 */
export const normalizeMenuPath = (path) => {
  if (typeof path !== "string") {
    return "";
  }

  const pathname = path.trim().split(/[?#]/)[0];

  if (!pathname) {
    return "";
  }

  const normalized = (pathname.startsWith("/") ? pathname : `/${pathname}`)
    .replace(/\/+/g, "/")
    .replace(/\/$/, "");

  return normalized || "/";
};

/**
 * 获取菜单页面进入时所需的全部权限。
 */
export const getMenuPermissionsForPath = (path) => {
  const routeConfig = menuRouteRegistry[normalizeMenuPath(path)];

  if (!routeConfig) {
    return [];
  }

  return [
    routeConfig.menuPermission,
    ...(routeConfig.permissionsAll || []),
  ].filter(Boolean);
};

/**
 * 判断实际路径是否符合动态路由模板。
 *
 * 例如：
 *
 * /tour/requirement/:requirementId
 * /tour/requirement/123
 */
const matchesRoutePattern = (pattern, targetPath) => {
  const patternSegments = normalizeMenuPath(pattern).split("/").filter(Boolean);

  const targetSegments = normalizeMenuPath(targetPath)
    .split("/")
    .filter(Boolean);

  return (
    patternSegments.length === targetSegments.length &&
    patternSegments.every(
      (segment, index) =>
        segment.startsWith(":") || segment === targetSegments[index],
    )
  );
};

/**
 * 将列表或详情地址解析为其菜单入口和必需权限。
 *
 * 主要用于登录重定向等尚未挂载动态路由的场景。
 */
export const getRouteAccessConfig = (path) => {
  const normalizedPath = normalizeMenuPath(path);
  const routeConfig = menuRouteRegistry[normalizedPath];

  // 普通菜单页面
  if (routeConfig) {
    return {
      menuPath: normalizedPath,
      permissionsAll: [
        routeConfig.menuPermission,
        ...(routeConfig.permissionsAll || []),
      ].filter(Boolean),
    };
  }

  // 详情页面
  for (const [menuPath, config] of Object.entries(menuRouteRegistry)) {
    const detailRoute = (config.detailRoutes || []).find((item) =>
      matchesRoutePattern(item.path, normalizedPath),
    );

    if (detailRoute) {
      return {
        menuPath,
        permissionsAll: [
          config.menuPermission,
          ...(detailRoute.permissionsAll || []),
        ].filter(Boolean),
      };
    }
  }

  return null;
};

/**
 * 是否为系统已注册的菜单地址。
 */
export const isKnownMenuRoute = (path) =>
  Boolean(menuRouteRegistry[normalizeMenuPath(path)]);

/**
 * 判断后端返回的菜单树中是否存在指定菜单路径。
 */
export const hasAccessibleMenuPath = (menus = [], targetPath) => {
  const normalizedTargetPath = normalizeMenuPath(targetPath);

  if (!isKnownMenuRoute(normalizedTargetPath)) {
    return false;
  }

  for (const menu of menus) {
    if (normalizeMenuPath(menu.path) === normalizedTargetPath) {
      return true;
    }

    if (
      menu.children?.length &&
      hasAccessibleMenuPath(menu.children, normalizedTargetPath)
    ) {
      return true;
    }
  }

  return false;
};

/**
 * 获取用户能够访问的第一个菜单地址。
 *
 * 一般用于：
 *
 * 登录后首页跳转
 * 当前路由无权限后的默认跳转
 */
export const getFirstAccessibleMenuPath = (menus = [], permissions = []) => {
  for (const menu of menus) {
    // 优先查找子菜单
    if (menu.children?.length) {
      const childPath = getFirstAccessibleMenuPath(menu.children, permissions);

      if (childPath) {
        return childPath;
      }
    }

    const menuPath = normalizeMenuPath(menu.path);
    const routeConfig = menuRouteRegistry[menuPath];

    if (
      routeConfig &&
      hasPermissionValue(permissions, routeConfig.menuPermission) &&
      (routeConfig.permissionsAll || []).every((item) =>
        hasPermissionValue(permissions, item),
      )
    ) {
      return menuPath;
    }
  }

  return "";
};

/**
 * 根据后端菜单树和当前用户权限生成动态路由。
 */
export const buildMenuRoutes = (menus = [], permissions = []) => {
  const routes = [];
  const seenPaths = new Set();

  const walk = (items = []) => {
    items.forEach((menu) => {
      const menuPath = normalizeMenuPath(menu.path);

      const routeConfig = menuRouteRegistry[menuPath];

      const hasMenuAccess =
        routeConfig &&
        hasPermissionValue(permissions, routeConfig.menuPermission);

      const canAccessRoute =
        hasMenuAccess &&
        (routeConfig.permissionsAll || []).every((item) =>
          hasPermissionValue(permissions, item),
        );

      // =====================================================
      // 菜单页面
      // =====================================================
      if (canAccessRoute && !seenPaths.has(menuPath)) {
        seenPaths.add(menuPath);

        routes.push({
          path: menuPath.slice(1),
          name: routeConfig.name,
          component: routeConfig.component,
          meta: {
            requiresAuth: true,
            title: menu.name || routeConfig.name,
            icon: menu.icon || "",
            menuId: menu.id,
            menuPath,
            permissionsAll: [
              routeConfig.menuPermission,
              ...(routeConfig.permissionsAll || []),
            ].filter(Boolean),
          },
        });
      }

      // =====================================================
      // 详情页面
      //
      // 详情权限和列表权限相互独立。
      //
      // 即：
      // 有菜单权限 + 详情权限
      // 即可注册详情路由，
      // 不强制要求拥有列表权限。
      // =====================================================
      if (hasMenuAccess) {
        (routeConfig.detailRoutes || []).forEach((detailRoute) => {
          const hasDetailPermission = (detailRoute.permissionsAll || []).every(
            (item) => hasPermissionValue(permissions, item),
          );

          if (!hasDetailPermission || seenPaths.has(detailRoute.path)) {
            return;
          }

          seenPaths.add(detailRoute.path);

          routes.push({
            path: detailRoute.path.slice(1),
            name: detailRoute.name,
            component: detailRoute.component,
            meta: {
              requiresAuth: true,
              title: detailRoute.title,
              menuPath,
              permissionsAll: [
                routeConfig.menuPermission,
                ...(detailRoute.permissionsAll || []),
              ].filter(Boolean),
            },
          });
        });
      }

      // =====================================================
      // 子菜单
      // =====================================================
      if (menu.children?.length) {
        walk(menu.children);
      }
    });
  };

  walk(menus);

  return routes;
};
